import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "nova_cart_v1";

/**
 * Cart structure: array of items.
 * Each item: { product_id, name, slug, image_url, varian, sub_varian, unit_price, quantity, status, sku }
 * Items with the same {product_id, varian, sub_varian} are merged (quantity summed).
 */
function keyOf(item) {
    return `${item.product_id}|${item.varian || ""}|${item.sub_varian || ""}`;
}

export function CartProvider({ children }) {
    const [items, setItems] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch { return []; }
    });

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
        } catch { /* localStorage full / disabled */ }
    }, [items]);

    const addItem = useCallback((item) => {
        setItems((prev) => {
            const k = keyOf(item);
            const idx = prev.findIndex((p) => keyOf(p) === k);
            if (idx >= 0) {
                const next = [...prev];
                next[idx] = { ...next[idx], quantity: next[idx].quantity + (item.quantity || 1) };
                return next;
            }
            return [...prev, { ...item, quantity: item.quantity || 1 }];
        });
    }, []);

    const updateQty = useCallback((k, qty) => {
        setItems((prev) => prev.map((p) => (keyOf(p) === k ? { ...p, quantity: Math.max(1, qty) } : p)));
    }, []);

    const removeItem = useCallback((k) => {
        setItems((prev) => prev.filter((p) => keyOf(p) !== k));
    }, []);

    const clear = useCallback(() => setItems([]), []);

    const total = items.reduce((sum, i) => sum + i.unit_price * i.quantity, 0);
    const count = items.reduce((sum, i) => sum + i.quantity, 0);

    return (
        <CartContext.Provider value={{ items, addItem, updateQty, removeItem, clear, total, count, keyOf }}>
            {children}
        </CartContext.Provider>
    );
}

export const useCart = () => {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used within CartProvider");
    return ctx;
};
